import styled from "styled-components";
import { COLORS } from "../../../values/colors";

export const ContentContainer = styled.div<{ $hasHeroBanner?: boolean }>`
  width: 92%;
  max-width: 1450px;
  margin: 0 auto 15rem;
  padding-top: ${({ $hasHeroBanner }) => ($hasHeroBanner ? "5.4rem" : "6.9rem")};

  @media (max-width: 1200px) {
    padding-top: ${({ $hasHeroBanner }) => ($hasHeroBanner ? "6.8rem" : "8.2rem")};
  }

  @media (max-width: 720px) {
    padding-top: ${({ $hasHeroBanner }) => ($hasHeroBanner ? "8rem" : "9.6rem")};
  }
`;

export const HeroSection = styled.section<{ $backgroundImage: string }>`
  position: relative;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  padding-top: 6.7rem;
  background:
    linear-gradient(to bottom, rgba(0, 0, 0, 0.58), rgba(0, 0, 0, 0.42), rgba(0, 0, 0, 0.62)),
    url(${({ $backgroundImage }) => $backgroundImage});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  @media (max-width: 1200px) {
    padding-top: 7.6rem;
  }

  @media (max-width: 720px) {
    padding-top: 9.1rem;
  }
`;

export const HeroPanel = styled.div`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 100%;
  min-height: calc(100vh - 6.7rem);
  display: flex;
  align-items: center;
  padding: 3.2rem clamp(1.5rem, 4vw, 3rem);

  &::after {
    content: "";
    position: absolute;
    inset: auto -8% -32% auto;
    width: 24rem;
    height: 24rem;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.03), transparent 68%);
    pointer-events: none;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 68% 34%, rgba(255, 255, 255, 0.03), transparent 18%),
      radial-gradient(circle at 58% 68%, rgba(255, 255, 255, 0.02), transparent 24%),
      linear-gradient(90deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.04) 36%, rgba(255, 255, 255, 0.015) 100%);
    pointer-events: none;
  }

  @media (max-width: 1200px) {
    min-height: calc(100vh - 7.6rem);
    padding: 2.5rem 1.35rem;
  }

  @media (max-width: 980px) {
    min-height: auto;
    padding: 2.25rem 2rem;
  }

  @media (max-width: 720px) {
    padding: 1.5rem 1rem;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  width: min(65rem, 58%);
  padding-left: clamp(0rem, 0.5vw, 0.45rem);

  @media (max-width: 980px) {
    width: min(100%, 44rem);
    padding-left: 0;
  }
`;

export const HeroEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(165, 214, 117, 0.2);
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
  background: rgba(129, 177, 94, 0.08);
  color: rgba(231, 244, 214, 0.9);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const HeroTitle = styled.h1`
  margin: 0;
  max-width: 15ch;
  font-size: clamp(2.5rem, 5vw, 4.9rem);
  line-height: 0.95;
  text-transform: uppercase;

  @media (max-width: 720px) {
    max-width: 10ch;
    font-size: clamp(2.2rem, 10vw, 3.35rem);
  }
`;

export const HeroText = styled.p`
  margin: 0;
  max-width: 42rem;
  color: rgba(235, 242, 232, 0.76);
  font-size: 1rem;
  line-height: 1.65;

  @media (max-width: 720px) {
    max-width: none;
    font-size: 0.96rem;
    line-height: 1.55;
  }
`;

export const HeroActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 0.35rem;

  @media (max-width: 560px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

export const HeroPrimaryAction = styled.button`
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #d6f0ae 0%, #9bcf67 100%);
  color: #0d150f;
  font-weight: 800;
  font-size: 0.9rem;
  letter-spacing: 0.02em;
  padding: 0.85rem 1.35rem;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.28);

  @media (max-width: 560px) {
    width: 100%;
  }
`;

export const HeroSecondaryAction = styled.button`
  border: 1px solid rgba(171, 219, 92, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(240, 248, 233, 0.94);
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.85rem 1.35rem;
  cursor: pointer;

  @media (max-width: 560px) {
    width: 100%;
  }
`;

export const HeroHighlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 0.5rem;
`;

export const HeroHighlight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

export const HeroHighlightValue = styled.span`
  font-size: 1.35rem;
  font-weight: 800;
`;

export const HeroHighlightLabel = styled.span`
  color: rgba(220, 233, 214, 0.66);
  font-size: 0.82rem;
`;

export const HeroVisual = styled.div`
  display: none;
`;

export const HeroImagePlate = styled.div`
  position: absolute;
  inset: 2rem 8% 0 8%;
  border-radius: 2rem;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.06);
  transform: rotate(-8deg);

  @media (max-width: 980px) {
    inset: 1rem 10% 0 10%;
  }
`;

export const HeroImage = styled.img`
  display: none;
`;

export const ProductArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const CategoryHeader = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  padding: 0.25rem 0 0.5rem;
`;

export const CategoryHeaderEyebrow = styled.span`
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CategoryHeaderTitle = styled.h1`
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 1;
  text-transform: uppercase;
`;

export const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(13rem, 18rem);
  gap: 0.9rem;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const ToolbarField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.38rem;
`;

export const ToolbarLabel = styled.label`
  color: rgba(255, 255, 255, 0.74);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const SearchInput = styled.input`
  border: 1px solid ${COLORS.gray};
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03));
  color: ${COLORS.white};
  border-radius: 0.95rem;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);

  &::placeholder {
    color: rgba(255, 255, 255, 0.46);
  }

  &:focus {
    outline: none;
    border-color: #9bcf67;
    box-shadow:
      0 0 0 3px rgba(155, 207, 103, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }
`;

export const SortSelect = styled.select`
  border: 1px solid ${COLORS.gray};
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03)),
    ${COLORS.darkdarkGray};
  color: ${COLORS.white};
  border-radius: 0.95rem;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  width: 100%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, rgba(255, 255, 255, 0.78) 50%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.78) 50%, transparent 50%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03));
  background-position:
    calc(100% - 1.15rem) calc(50% - 0.12rem),
    calc(100% - 0.8rem) calc(50% - 0.12rem),
    0 0;
  background-size:
    0.42rem 0.42rem,
    0.42rem 0.42rem,
    100% 100%;
  background-repeat: no-repeat;

  &:focus {
    outline: none;
    border-color: #9bcf67;
    box-shadow:
      0 0 0 3px rgba(155, 207, 103, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }

  option {
    color: ${COLORS.white};
    background-color: #141414;
  }
`;

export const CartToggleButton = styled.button`
  border: 0;
  border-radius: 0.8rem;
  background-color: ${COLORS.apronBrown};
  color: ${COLORS.white};
  font-weight: 700;
  padding: 0.8rem 1.2rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
`;

export const ResultsText = styled.p`
  margin: 0;
  color: ${COLORS.textSecondary};
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 1.25rem;

  @media (max-width: 560px) {
    gap: 1rem;
  }
`;

export const ProductCard = styled.article`
  border-radius: 2rem;
  padding: 1.2rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  min-height: 28rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 32px rgba(0, 0, 0, 0.45);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.white};
    outline-offset: 3px;
  }

  @media (max-width: 560px) {
    min-height: auto;
    border-radius: 1.35rem;
    padding: 1rem;
  }
`;

export const ProductImageArea = styled.div`
  border-radius: 1.5rem;
  min-height: 13rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 1rem;
`;

export const ProductEmblaViewport = styled.div`
  width: 100%;
  overflow: hidden;
`;

export const ProductEmblaContainer = styled.div`
  width: 100%;
  display: flex;
`;

export const ProductEmblaSlide = styled.div`
  width: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ProductImage = styled.img`
  max-width: 100%;
  max-height: 13rem;
  object-fit: contain;
  filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.4));
`;

export const ProductImagePlaceholder = styled.div`
  width: 100%;
  min-height: 13rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
  padding: 0.75rem;

  img {
    width: 100%;
    height: auto;
    max-width: 14rem;
  }
`;

export const DotRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.45rem;
`;

export const Dot = styled.button<{ $active?: boolean }>`
  border: 1px solid ${COLORS.darkdarkGray};
  width: 0.48rem;
  height: 0.48rem;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? COLORS.darkdarkGray : "transparent")};
  padding: 0;
  cursor: pointer;
`;

export const ProductName = styled.h3`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.15;
  text-align: center;

  @media (max-width: 560px) {
    font-size: 1.35rem;
  }
`;

export const ProductDescription = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  min-height: 2.8rem;

  @media (max-width: 560px) {
    min-height: 0;
  }
`;

export const ProductPrice = styled.p`
  margin: 0;
  font-size: 1.75rem;
  text-align: center;
  font-weight: 800;

  @media (max-width: 560px) {
    font-size: 1.5rem;
  }
`;

export const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const OptionLabel = styled.label`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
`;

export const OptionSelect = styled.select`
  background-color: rgba(0, 0, 0, 0.23);
  color: ${COLORS.white};
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 0.7rem;
  padding: 0.55rem;

  option {
    color: black;
  }
`;

export const OptionTextInput = styled.input`
  background-color: rgba(0, 0, 0, 0.23);
  color: ${COLORS.white};
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 0.7rem;
  padding: 0.55rem;
`;

export const OptionValidationText = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #ffafaf;
`;

export const CardFooter = styled.div`
  margin-top: auto;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.6rem;
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;

  @media (max-width: 560px) {
    justify-content: center;
  }
`;

export const QuantityButton = styled.button`
  border: 0;
  background: transparent;
  color: ${COLORS.white};
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
`;

export const QuantityValue = styled.span`
  min-width: 1.2rem;
  text-align: center;
  font-size: 1.15rem;
`;

export const AddButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: ${COLORS.white};
  color: rgba(17, 24, 32, 0.72);
  font-weight: 700;
  padding: 0.65rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 5px 8px rgba(0, 0, 0, 0.18);

  &:hover {
    background: rgba(255, 255, 255, 0.92);
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    box-shadow: none;
  }

  @media (max-width: 560px) {
    width: 100%;
  }
`;

export const DetailButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  background: transparent;
  color: ${COLORS.white};
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
`;

export const EmptyResult = styled.div`
  border: 1px dashed ${COLORS.gray};
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  color: ${COLORS.textSecondary};
`;

export const Overlay = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.42);
  z-index: 19;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.3s ease;
`;

export const CartSlider = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(27rem, 94vw);
  height: 100dvh;
  border-left: 1px solid ${COLORS.gray};
  background-color: ${COLORS.darkdarkGray};
  z-index: 20;
  padding: 1.2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transform: translateX(${({ $open }) => ($open ? "0%" : "100%")});
  transition: transform 0.32s ease;
`;

export const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CartTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

export const CloseCartButton = styled.button`
  border: 0;
  background: transparent;
  color: ${COLORS.white};
  font-size: 1.6rem;
  cursor: pointer;
`;

export const EmptyText = styled.p`
  margin: 0;
  color: ${COLORS.textSecondary};
`;

export const CartList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.3rem;
`;

export const CartItem = styled.div`
  border: 1px solid ${COLORS.gray};
  border-radius: 0.65rem;
  padding: 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const CartItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
`;

export const CartItemName = styled.p`
  margin: 0;
  font-weight: 600;
`;

export const CartItemPrice = styled.p`
  margin: 0;
  font-weight: 700;
`;

export const CartItemOptions = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${COLORS.textSecondary};
`;

export const RemoveButton = styled.button`
  margin-left: auto;
  border: 0;
  background: transparent;
  color: ${COLORS.textSecondary};
  cursor: pointer;
  text-decoration: underline;
`;

export const CartSummary = styled.div`
  border-top: 1px solid ${COLORS.gray};
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: auto;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const CheckoutHint = styled.p`
  margin: 0;
  color: ${COLORS.textSecondary};
  font-size: 0.84rem;
  line-height: 1.35;
`;

export const CheckoutButton = styled.button`
  border: 0;
  border-radius: 0.5rem;
  background-color: ${COLORS.apronBrown};
  color: ${COLORS.white};
  font-weight: 700;
  padding: 0.7rem;
  cursor: pointer;
`;

export const AddModalOverlay = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 24;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.2s ease;
`;

export const AddModal = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? "1" : "0.98")});
  width: min(34rem, 92vw);
  max-height: 88dvh;
  overflow-y: auto;
  border: 1px solid ${COLORS.gray};
  border-radius: 1rem;
  background-color: ${COLORS.darkdarkGray};
  z-index: 25;
  padding: 1rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

export const AddModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

export const AddModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
`;

export const AddModalCloseButton = styled.button`
  border: 0;
  background: transparent;
  color: ${COLORS.white};
  font-size: 1.5rem;
  cursor: pointer;
`;

export const AddModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGridFull = styled.div`
  grid-column: 1 / -1;
`;

export const DialogIntroText = styled.p`
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.96rem;
  line-height: 1.6;
`;

export const DialogField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const DialogFieldLabel = styled.span`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.62);
`;

export const DialogInput = styled.input`
  border: 1.5px solid rgba(255, 255, 255, 0.28);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.04);
  color: ${COLORS.white};
  padding: 0.8rem 0.9rem;
  font-size: 0.96rem;
  font-family: inherit;

  &::placeholder {
    color: rgba(255, 255, 255, 0.38);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255);
  }
`;

export const DialogTextArea = styled.textarea`
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.04);
  color: ${COLORS.white};
  padding: 0.8rem 0.9rem;
  font-size: 0.96rem;
  font-family: inherit;
  min-height: 7rem;
  resize: vertical;

  &::placeholder {
    color: rgba(255, 255, 255, 0.38);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.88);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.16);
  }
`;

export const DialogPrimaryButton = styled.button`
  border-radius: 999px;
  padding: 0.75rem 1.15rem;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid rgba(181, 115, 76, 0.3);
  background: linear-gradient(135deg, ${COLORS.apronBrown} 0%, ${COLORS.apronBrownDark} 100%);
  color: ${COLORS.white};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DialogSecondaryButton = styled.button`
  border-radius: 999px;
  padding: 0.75rem 1.15rem;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.03);
  color: ${COLORS.white};
`;
